import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useDebouncedValue } from './useDebouncedValue';

type VisibleProviderProps = {
  children: React.ReactNode;
  delay?: number;
};

type VisibleContextValue = {
  setContainerNode: (node: HTMLElement | null) => void;
  containerRect: DOMRect | null;
  version: number;
};

const VisibleContext = createContext<VisibleContextValue | null>(null);

const isElementVisible = (
  containerRect: DOMRect,
  elementRect: DOMRect | null
) => {
  if (!elementRect || !elementRect.width || !elementRect.height) {
    return false;
  }

  const isInVerticalRange =
    elementRect.bottom > containerRect.top &&
    elementRect.top < containerRect.bottom;
  const isInHorizontalRange =
    elementRect.right > containerRect.left &&
    elementRect.left < containerRect.right;

  return isInVerticalRange && isInHorizontalRange;
};

const getContainerRect = (container: HTMLElement | null): DOMRect | null =>
  container ? container.getBoundingClientRect() : null;

export const VisibleProvider: React.FC<VisibleProviderProps> = ({
  children,
  delay = 50,
}) => {
  const [containerNode, setContainerNode] = useState<HTMLElement | null>(null);
  const [rawContainerRect, setRawContainerRect] = useState<DOMRect | null>(
    null
  );
  const [version, setVersion] = useState(0);

  const debouncedRect = useDebouncedValue(rawContainerRect, delay);

  useEffect(() => {
    setVersion(prev => prev + 1);
  }, [debouncedRect]);

  const refreshContainerRect = useCallback(() => {
    setRawContainerRect(getContainerRect(containerNode));
  }, [containerNode]);

  const handleSetContainerNode = useCallback((node: HTMLElement | null) => {
    setContainerNode(node);
    setRawContainerRect(getContainerRect(node));
  }, []);

  useEffect(() => {
    refreshContainerRect();

    const container = containerNode;
    if (!container) {
      return undefined;
    }

    container.addEventListener('scroll', refreshContainerRect, {
      passive: true,
    });
    window.addEventListener('resize', refreshContainerRect);

    return () => {
      container.removeEventListener('scroll', refreshContainerRect);
      window.removeEventListener('resize', refreshContainerRect);
    };
  }, [containerNode, refreshContainerRect]);

  return (
    <VisibleContext.Provider
      value={{
        setContainerNode: handleSetContainerNode,
        containerRect: debouncedRect,
        version,
      }}
    >
      {children}
    </VisibleContext.Provider>
  );
};

const useVisibleContext = () => {
  const context = useContext(VisibleContext);
  if (!context) {
    throw new Error('useVisibleItem must be used within a VisibleProvider');
  }
  return context;
};

export const useVisibleContainer = () => {
  const { setContainerNode } = useVisibleContext();
  return { setContainerNode };
};

export const useVisibleItem = () => {
  const { containerRect, version } = useVisibleContext();
  const [elementNode, setElementNode] = useState<HTMLElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!containerRect || !elementNode) {
      setVisible(false);
      return;
    }

    setVisible(
      isElementVisible(containerRect, elementNode.getBoundingClientRect())
    );
  }, [containerRect, elementNode, version]);

  return {
    visible,
    setElementNode,
  };
};
